import {
  SchedulerThemeId,
  isSchedulerThemeId,
  isSchedulerToken,
} from './scheduler-theme-tokens';

/**
 * A user's tweak of one of the shipped directions, and the string codec that
 * lets them mail it to someone else.
 *
 * There is deliberately no server in this: the scheduler is served over plain
 * http on the factory floor, so sharing is a copy/paste of one line of text,
 * and nothing persists anywhere but the author's own localStorage.
 *
 * A shared string names its base direction and raw `--sched-*` variables, so
 * those ids are a contract with every theme already pasted around: retiring a
 * direction rejects imports built on it and silently drops saved tweaks of it
 * (see `readCustomThemes`) unless the old id is mapped to a survivor first.
 */

export interface SchedulerCustomTheme {
  /** Stable local key. Not shared — an imported theme is given a fresh one. */
  id: string;
  name: string;
  /** The shipped direction this is a tweak of. Unedited tokens fall through to it. */
  base: SchedulerThemeId;
  /** Only the tokens the author actually changed. */
  vars: Record<string, string>;
}

/** Bumped only if the payload shape changes in a way v1 readers cannot take. */
const PAYLOAD_VERSION = 1;

/**
 * Base64 rather than raw JSON, because these get pasted into email and Teams,
 * which wrap long lines and helpfully convert quotes to smart quotes. The
 * prefix is there so a recipient can tell what the blob is, and so the import
 * box can reject a stray paste with a useful message instead of a parse error.
 */
const PAYLOAD_PREFIX = 'eclipse-scheduler-theme-v1:';

const MAX_NAME_LENGTH = 60;

/**
 * A pasted value ends up in `style.setProperty` on `<html>`, so it is checked
 * rather than trusted. `setProperty` will not parse a declaration out of a
 * value — a stray `;` just makes it invalid — but `url()` would still let a
 * shared theme phone home from someone else's browser, which is not something
 * a color should be able to do. Backslashes are refused outright: a CSS escape
 * (`\75rl(`) spells `url(` without matching any pattern here, and no color or
 * length needs one.
 */
const UNSAFE_VALUE = /url\(|image-set\(|expression\(|@import|javascript:|[;{}<>\\]/i;

const MAX_VALUE_LENGTH = 200;

export function isSafeThemeValue(value: string): boolean {
  return value.length > 0 && value.length <= MAX_VALUE_LENGTH && !UNSAFE_VALUE.test(value);
}

export function newCustomThemeId(): string {
  return `custom-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

function toBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function fromBase64(base64: string): string {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** The one line a user copies out of the editor. */
export function encodeCustomTheme(theme: SchedulerCustomTheme): string {
  const payload = {
    v: PAYLOAD_VERSION,
    name: theme.name,
    base: theme.base,
    vars: theme.vars,
  };
  return PAYLOAD_PREFIX + toBase64(JSON.stringify(payload));
}

/**
 * A string discriminant rather than an `ok: boolean`, because this project
 * compiles without `strictNullChecks` and TypeScript will not narrow a boolean
 * discriminant in that mode — `if (!result.ok)` leaves you holding the whole
 * union.
 */
export type CustomThemeDecodeResult =
  | {
      status: 'ok';
      theme: SchedulerCustomTheme;
      /**
       * Variable names that were not in the catalog, so the editor can say how
       * many were ignored. Expected when a theme is shared backwards from a
       * newer build, and the honest answer there is "some of this did not
       * apply" rather than a silent partial import.
       */
      dropped: string[];
    }
  | { status: 'error'; error: string };

/**
 * Read a pasted theme.
 *
 * Tolerant on the way in, because the text has been through a mail client:
 * surrounding whitespace and injected line breaks are stripped, the prefix is
 * optional, and raw JSON is accepted too — that last one is mostly for anyone
 * hand-writing a payload.
 */
export function decodeCustomTheme(text: string): CustomThemeDecodeResult {
  const trimmed = text.trim();
  if (!trimmed) {
    return { status: 'error', error: 'Nothing to import — paste a theme string first.' };
  }

  // Mail clients wrap long lines; the payload never contains whitespace itself.
  const compact = trimmed.replace(/\s+/g, '');
  const body = compact.startsWith(PAYLOAD_PREFIX)
    ? compact.slice(PAYLOAD_PREFIX.length)
    : compact;

  let json: string;
  if (body.startsWith('{')) {
    json = trimmed;
  } else {
    try {
      json = fromBase64(body);
    } catch {
      return {
        status: 'error',
        error: 'That does not look like a theme string. Copy the whole line, including the prefix.',
      };
    }
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { status: 'error', error: 'That theme string is damaged — it may have been cut short.' };
  }

  if (!parsed || typeof parsed !== 'object') {
    return { status: 'error', error: 'That theme string is damaged — it may have been cut short.' };
  }

  const payload = parsed as { v?: unknown; name?: unknown; base?: unknown; vars?: unknown };

  if (payload.v !== PAYLOAD_VERSION) {
    return { status: 'error', error: 'That theme was made by a different version of the scheduler.' };
  }
  if (!isSchedulerThemeId(payload.base)) {
    return { status: 'error', error: 'That theme is built on a color direction this build does not have.' };
  }
  if (!payload.vars || typeof payload.vars !== 'object') {
    return { status: 'error', error: 'That theme string is damaged — it has no colors in it.' };
  }

  const vars: Record<string, string> = {};
  const dropped: string[] = [];
  for (const [name, value] of Object.entries(payload.vars as Record<string, unknown>)) {
    if (typeof value !== 'string' || !isSchedulerToken(name) || !isSafeThemeValue(value)) {
      dropped.push(name);
      continue;
    }
    vars[name] = value;
  }

  const name =
    typeof payload.name === 'string' && payload.name.trim()
      ? payload.name.trim().slice(0, MAX_NAME_LENGTH)
      : 'Imported theme';

  return {
    status: 'ok',
    theme: { id: newCustomThemeId(), name, base: payload.base, vars },
    dropped,
  };
}

/** `Ricky's tweak` → `rickys-tweak`, for the generated stylesheet block. */
export function slugifyThemeName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'custom';
}

/**
 * Turn a tweak worth shipping into a block that pastes straight into
 * `_scheduler-theme.scss` next to the shipped directions. It also needs an
 * entry in `SCHEDULER_THEMES` to appear in the cog menu.
 */
export function customThemeToScss(theme: SchedulerCustomTheme): string {
  const names = Object.keys(theme.vars).sort();
  const lines = [
    `/* "${theme.name}" — a tweak of the "${theme.base}" direction.`,
    `   Generated by the scheduler theme editor. Only the changed variables are`,
    `   listed; everything else still comes from "${theme.base}". */`,
    `:root[data-scheduler-theme='${slugifyThemeName(theme.name)}'] {`,
  ];
  if (!names.length) {
    lines.push(`  /* Nothing changed. */`);
  }
  for (const name of names) {
    lines.push(`  ${name}: ${theme.vars[name]};`);
  }
  lines.push('}');
  return lines.join('\n');
}

/**
 * Normalize any CSS color to the `#rrggbb` that `<input type="color">` insists
 * on, via the canvas trick — a detached element has no computed style to read,
 * and the values in the stylesheet are a mix of hex, `rgb()`, `rgba()` and
 * bare keywords like `red` and `transparent`.
 *
 * Returns null for anything that is not a color (`none`, a `var()` the editor
 * could not resolve), which is the editor's cue to show the swatch as empty and
 * leave the text field in charge.
 */
let swatchContext: CanvasRenderingContext2D | null | undefined;

export function colorToHexSwatch(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (swatchContext === undefined) {
    swatchContext = document.createElement('canvas').getContext('2d');
  }
  if (!swatchContext) {
    return /^#[0-9a-f]{6}$/i.test(trimmed) ? trimmed.toLowerCase() : null;
  }

  // An invalid assignment is ignored rather than throwing, so park a sentinel
  // first and treat "unchanged" as "not a color".
  const sentinel = '#010203';
  swatchContext.fillStyle = sentinel;
  swatchContext.fillStyle = trimmed;
  const normalized = swatchContext.fillStyle as string;
  if (normalized === sentinel && trimmed.toLowerCase() !== sentinel) {
    return null;
  }

  if (/^#[0-9a-f]{6}$/i.test(normalized)) {
    return normalized.toLowerCase();
  }

  // Translucent colors normalize to `rgba(r, g, b, a)`. The swatch can only
  // show the hue, which is honest enough — the text field keeps the alpha.
  const rgba = normalized.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!rgba) {
    return null;
  }
  const hex = (n: string) => Number(n).toString(16).padStart(2, '0');
  return `#${hex(rgba[1])}${hex(rgba[2])}${hex(rgba[3])}`;
}
